import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import HTMLOrdinalRender from 'components/answers/FileEmbed/HTMLOrdinalRender'
import { ordinalWalletBridgeEnabled } from 'lib/ordinalWalletBridge/protocol'
import MediaPlayButton from 'components/shared/MediaPlayButton'
import { dispatchUrlPreview, fetchUrlPreviewByAddress, requestUrlPreviewGeneration } from 'api/api'
import { Maximize2, Square, X } from 'lucide-react'
import { announceTopLayerChange } from 'lib/topLayer'
import { isTrayOpenIn } from 'components/shared/trayContainer'
import useStore from 'state/knovStore'
import {
    shouldDispatchServerPreview,
    type OrdinalPreviewDispatchDecision,
    type OrdinalPreviewReadyOptions,
} from 'components/shared/ordinalPreviewDispatch'
import styles from './html-ordinal-loader.module.scss'

interface HTMLOrdinalLoaderProps {
    src: string
    /** Optional canonical address to use for URL preview lookup/generation (instead of src). */
    previewAddress?: string
    className?: string
    allowInteraction?: boolean
    title?: string
    htmlAlign?: 'center' | 'top-center'
    /** Keep a stable screenshot shell until the user explicitly activates live HTML. */
    passivePreview?: boolean
    /** Allow address-based screenshot lookup/generation for this mounted shell. */
    previewLookupEnabled?: boolean
    previewUrl?: string
    answerId?: string
    /** The post (and its thread) embedding this ordinal, told to the running ordinal. */
    walletPost?: { answerId: string; threadId?: string | null } | null
    setPostDisabled?: (disabled: boolean) => void
    ensureUrl?: boolean
    forceLive?: boolean
    onRequestLive?: () => void
    onPreviewPending?: () => void
    onPreviewReady?: (options?: OrdinalPreviewReadyOptions) => void
    fromLinkPreview?: boolean
    previewDispatchDecision?: OrdinalPreviewDispatchDecision
}

// Fullscreen promotes the viewer into the browser's top layer with `dialog.showModal()`. The
// Fullscreen button only appears where that exists (Safari 15.4+, Chrome 37+): without the top
// layer a post-level overlay cannot reach the viewport (panels carry a transform, so `position:
// fixed` resolves against the panel), and a second iframe elsewhere would be a second runtime.
const supportsTopLayerDialog =
    typeof HTMLDialogElement !== 'undefined' &&
    typeof HTMLDialogElement.prototype.showModal === 'function'

// Long enough for the click a touch tap synthesises at touchend to have been dispatched.
const TOUCH_SETTLE_MS = 400

const HTMLOrdinalLoader: React.FC<HTMLOrdinalLoaderProps> = ({
    src,
    previewAddress,
    className,
    allowInteraction = true,
    title = 'Ordinal Preview',
    htmlAlign = 'top-center',
    passivePreview = false,
    previewLookupEnabled = true,
    previewUrl,
    answerId,
    walletPost,
    setPostDisabled,
    ensureUrl = false,
    forceLive = false,
    onRequestLive,
    onPreviewPending,
    onPreviewReady,
    fromLinkPreview = false,
    previewDispatchDecision = 'dispatch',
}) => {
    const liveEmbedSrc = useMemo(() => {
        const outpointMatch = (src || '').match(/([0-9a-f]{64}_[0-9]+)/i)
        if (outpointMatch?.[1]) {
            return `https://ordinals.gorillapool.io/content/${outpointMatch[1]}`
        }
        const previewOutpointMatch = (previewAddress || '').match(/([0-9a-f]{64}_[0-9]+)/i)
        if (previewOutpointMatch?.[1]) {
            return `https://ordinals.gorillapool.io/content/${previewOutpointMatch[1]}`
        }
        return src
    }, [src, previewAddress])
    // Set below, once the state it resets exists.
    const stopForOtherOrdinalRef = useRef(() => {})
    const walletBridge = useMemo(() => {
        if (!ordinalWalletBridgeEnabled()) return null
        const origin = liveEmbedSrc.match(/\/content\/([0-9a-f]{64}_[0-9]+)$/i)?.[1]
        if (!origin) return null
        const post = walletPost || (answerId ? { answerId } : null)
        return {
            origin: origin.toLowerCase(),
            answerId: post?.answerId,
            threadId: post?.threadId,
            // Another ordinal started and took the wallet: back to the static preview.
            onStop: () => stopForOtherOrdinalRef.current(),
        }
    }, [liveEmbedSrc, answerId, walletPost])

    const generationRequestedRef = useRef<Set<string>>(new Set())
    const prevSrcRef = useRef<string | null>(null)
    const userActivatedRef = useRef<boolean>(forceLive && !passivePreview)
    const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState<string | null>(previewUrl || null)
    // Prevent duplicate server-side screenshot dispatches for the same src within a single mount.
    // (If dispatch throws, we still want to avoid spamming on every render.)
    const dispatchedRef = useRef<boolean>(false)
    // Ensure we only respond to preview <img> load failures once per src, otherwise a persistent
    // 403/404 can cause repeated state flips and regen attempts.
    const previewErrorHandledRef = useRef<Set<string>>(new Set())
    const [showLiveEmbed, setShowLiveEmbed] = useState<boolean>(forceLive && !passivePreview)
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
    const viewerDialogRef = useRef<HTMLDialogElement | null>(null)
    const setViewerDialogRef = useCallback((node: HTMLDialogElement | null) => {
        // The viewer can unmount while open (Stop from fullscreen with a screenshot to fall back
        // to). Close it first, while it is still in the tree, so the browser runs its own close
        // steps — top-layer exit — instead of having an open modal yanked out.
        if (!node && viewerDialogRef.current) {
            // A prompt shown inside the viewer would be left in a detached element: decline it.
            const state = useStore.getState()
            if (isTrayOpenIn(state, viewerDialogRef.current)) state.actions.trayActions.closeTray()
        }
        if (!node && viewerDialogRef.current?.open) {
            viewerDialogRef.current.close()
            announceTopLayerChange()
        }
        viewerDialogRef.current = node
    }, [])
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const fullscreenButtonRef = useRef<HTMLButtonElement | null>(null)
    // True while the viewer has been promoted, so the exit branch runs only on a real exit.
    const promotedRef = useRef<boolean>(false)
    // Set when the exit came from the keyboard (Escape, Enter on a control): the inline control
    // gets focus back, so a keyboard user keeps their place in the thread. A pointer exit leaves
    // focus alone — WebKit draws a focus ring on programmatic focus after a tap.
    const restoreFocusOnExitRef = useRef<boolean>(false)
    // A touch activation mounts the interactive iframe under the finger; the click the browser
    // synthesises at touchend would land inside the freshly loaded ordinal. Hold interaction off
    // until that tap has settled.
    const [touchSettling, setTouchSettling] = useState<boolean>(false)
    const touchSettleTimerRef = useRef<number | null>(null)
    useEffect(
        () => () => {
            if (touchSettleTimerRef.current !== null) {
                window.clearTimeout(touchSettleTimerRef.current)
            }
        },
        [],
    )
    // Set by Stop, cleared by play. Lets a passive card, which shows nothing while its screenshot
    // is pending, still hold the play control after Stop.
    const [stoppedByUser, setStoppedByUser] = useState<boolean>(false)
    const [previewLoadFailed, setPreviewLoadFailed] = useState<boolean>(false)
    stopForOtherOrdinalRef.current = () => {
        userActivatedRef.current = false
        setIsFullscreen(false)
        setShowLiveEmbed(false)
        setStoppedByUser(true)
    }
    const previewAvailable = !!resolvedPreviewUrl

    useEffect(() => {
        if (prevSrcRef.current === src) return
        prevSrcRef.current = src
        dispatchedRef.current = false
        previewErrorHandledRef.current.delete(src)
        userActivatedRef.current = forceLive && !passivePreview
        setShowLiveEmbed(forceLive && !passivePreview)
        setResolvedPreviewUrl(previewUrl || null)
        setIsFullscreen(false)
        setStoppedByUser(false)
        setPreviewLoadFailed(false)
    }, [src, previewUrl, forceLive, passivePreview])

    useEffect(() => {
        if (forceLive && !passivePreview) {
            userActivatedRef.current = true
            setShowLiveEmbed(true)
        }
    }, [forceLive, passivePreview])

    useEffect(() => {
        if (!passivePreview) return
        userActivatedRef.current = false
        setShowLiveEmbed(false)
        setIsFullscreen(false)
    }, [passivePreview])

    // Fullscreen is the SAME viewer element promoted into the top layer, never a second render:
    // moving an iframe in the DOM (a portal re-target included) reloads it in Chromium and WebKit,
    // and two mounted copies would run the ordinal twice. `showModal()` escapes the transformed
    // panel ancestors without touching the iframe, so the ordinal keeps its state across enter and
    // exit. Layout effect: the controls swap and the top-layer promotion land in one frame.
    useLayoutEffect(() => {
        const dialog = viewerDialogRef.current
        if (isFullscreen) {
            if (!dialog || dialog.open) return
            try {
                dialog.showModal()
            } catch {
                setIsFullscreen(false)
                return
            }
            promotedRef.current = true
            // `showModal()` hands focus to the first focusable descendant — the iframe — where a
            // cross-origin document would swallow Escape. Park it on the viewer itself instead
            // (no focus ring to draw on a touch screen, unlike the exit button); a tap into the
            // ordinal still takes focus the way it does inline.
            dialog.focus({ preventScroll: true })
            announceTopLayerChange()
            return
        }
        if (!promotedRef.current) return
        promotedRef.current = false
        if (dialog?.open) dialog.close()
        announceTopLayerChange()
        if (restoreFocusOnExitRef.current) {
            restoreFocusOnExitRef.current = false
            // The Fullscreen button when the viewer is still live; the play control after Stop.
            const control =
                fullscreenButtonRef.current ||
                wrapperRef.current?.querySelector<HTMLButtonElement>(
                    'button[aria-label="Load live preview"]',
                )
            control?.focus({ preventScroll: true })
        }
    }, [isFullscreen])

    // Escape is handled here, at the window capture phase, ahead of every other listener. Left to
    // the dialog's own close request, the keydown would first bubble through ancestors that treat
    // Escape as theirs: react-modal's content handler (the wallet Ordinals viewer, the reply modal)
    // closes the whole modal, and the composer's quote handler preventDefault()s it, which cancels
    // the dialog's close request outright. Keys pressed with focus inside the ordinal's document
    // never reach this window at all; the Exit control covers that case.
    useEffect(() => {
        if (!isFullscreen) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            event.stopPropagation()
            // A wallet prompt shown inside the viewer: Escape declines it and stays fullscreen.
            const state = useStore.getState()
            if (isTrayOpenIn(state, viewerDialogRef.current)) {
                event.preventDefault()
                state.actions.trayActions.closeTray()
                return
            }
            restoreFocusOnExitRef.current = true
            setIsFullscreen(false)
        }
        window.addEventListener('keydown', onKeyDown, true)

        return () => window.removeEventListener('keydown', onKeyDown, true)
    }, [isFullscreen])

    // Fullscreen UX: lock page scrolling behind the modal viewer.
    useEffect(() => {
        if (!isFullscreen) return

        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = prevOverflow
        }
    }, [isFullscreen])

    useEffect(() => {
        if (!previewUrl) {
            setResolvedPreviewUrl(null)
            if (!forceLive && !userActivatedRef.current) {
                setShowLiveEmbed(false)
            }
            return
        }
        setResolvedPreviewUrl(previewUrl)
        setPreviewLoadFailed(false)
        if (!forceLive && !userActivatedRef.current) {
            setShowLiveEmbed(false)
        }
        onPreviewReady?.({ previewUrl, source: 'prop' })
    }, [previewUrl, forceLive, onPreviewReady])

    useEffect(() => {
        if (!previewLookupEnabled || forceLive || answerId || !src || previewUrl) return
        let cancelled = false

        const run = async () => {
            try {
                const addr = previewAddress || src
                onPreviewPending?.()
                const existing = await fetchUrlPreviewByAddress(addr)
                if (cancelled) return
                const previewUrlFromExisting = existing?.ok ? existing.preview_image_url : null

                if (previewUrlFromExisting) {
                    setResolvedPreviewUrl(previewUrlFromExisting)
                    if (!forceLive && !userActivatedRef.current) {
                        setShowLiveEmbed(false)
                    }
                    onPreviewReady?.({ previewUrl: previewUrlFromExisting, source: 'existing' })
                } else {
                    setResolvedPreviewUrl(null)
                    if (!forceLive && !userActivatedRef.current) {
                        setShowLiveEmbed(false)
                    }
                }

                if (!previewUrlFromExisting && !generationRequestedRef.current.has(addr)) {
                    generationRequestedRef.current.add(addr)
                    // Generation is asynchronous and persists the screenshot on the Url. Keep
                    // this mount stable; a later render will retrieve it through the lookup above.
                    await requestUrlPreviewGeneration(addr)
                }
            } catch (e) {
                if (!forceLive && !userActivatedRef.current) {
                    setShowLiveEmbed(false)
                }
            }
        }

        run()

        return () => {
            cancelled = true
        }
    }, [
        answerId,
        src,
        forceLive,
        previewUrl,
        fromLinkPreview,
        onPreviewPending,
        onPreviewReady,
        previewLookupEnabled,
    ])

    // Dispatch server-side screenshot when no preview and we have an answerId to receive socket events
    useEffect(() => {
        if (
            !shouldDispatchServerPreview({
                answerId,
                previewAvailable,
                dispatched: dispatchedRef.current,
                decision: previewDispatchDecision,
                previewLoadFailed,
            })
        ) {
            return
        }
        const addr = previewAddress || src
        // Guard: never send ScreenshotOne against our internal ordinal data endpoint.
        // It redirects to binary content and will spam ScreenshotOne / fail on refresh.
        if (/\/api\/v1\/bsv\/ordinals\/[0-9a-fA-F-]+\/data\b/.test(addr)) {
            dispatchedRef.current = true
            return
        }
        // Disable Post button while generating
        try {
            setPostDisabled && setPostDisabled(true)
        } catch (e) {}
        onPreviewPending?.()
        // Mark as dispatched BEFORE awaiting network, so failures can't spam re-renders.
        dispatchedRef.current = true
        ;(async () => {
            try {
                await dispatchUrlPreview({
                    answerId,
                    address: addr,
                    ensureUrl,
                })
            } catch (e) {
                // Keep dispatchedRef.current = true to avoid thundering-herd retries.
                // If we want explicit retries, do it with a controlled backoff + cap.
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        answerId,
        src,
        previewAddress,
        previewAvailable,
        ensureUrl,
        previewDispatchDecision,
        previewLoadFailed,
    ])

    const shouldShowPreview = !!resolvedPreviewUrl && !showLiveEmbed
    // Live HTML only ever mounts after the user presses play. An iframe is a running document —
    // an ordinal's `<audio autoplay>` sounds the moment it loads — so it can never stand in as a
    // first frame, even with interaction blocked.
    const shouldShowLiveSurface = showLiveEmbed
    // Nothing static to show: a placeholder holds the play control. Passive stream surfaces keep
    // their reserved frame blank while a screenshot is pending, until the user has stopped them.
    const shouldShowPlaceholder =
        !shouldShowPreview && !shouldShowLiveSurface && (!passivePreview || stoppedByUser)
    const handleRequestLive = (event: React.MouseEvent | React.TouchEvent) => {
        event.stopPropagation()
        userActivatedRef.current = true
        setStoppedByUser(false)
        setShowLiveEmbed(true)
        if (event.type === 'touchstart') {
            setTouchSettling(true)
            if (touchSettleTimerRef.current !== null) {
                window.clearTimeout(touchSettleTimerRef.current)
            }
            touchSettleTimerRef.current = window.setTimeout(() => {
                touchSettleTimerRef.current = null
                setTouchSettling(false)
            }, TOUCH_SETTLE_MS)
        }
    }

    const handleEnterFullscreen = (event: React.MouseEvent) => {
        event.stopPropagation()
        event.preventDefault()
        setIsFullscreen(true)
    }

    const handleStopLive = (event: React.MouseEvent) => {
        event.stopPropagation()
        event.preventDefault()
        // A click without a pointer (`detail` 0) is a keyboard activation.
        restoreFocusOnExitRef.current = event.detail === 0
        userActivatedRef.current = false
        setIsFullscreen(false)
        setShowLiveEmbed(false)
        setStoppedByUser(true)
    }

    const handleExitFullscreen = (event: React.MouseEvent) => {
        event.stopPropagation()
        event.preventDefault()
        restoreFocusOnExitRef.current = event.detail === 0
        setIsFullscreen(false)
    }

    // The dialog is closing on its own (a platform close request): keep state in step so the
    // inline controls come back and the scroll lock lifts. Wired to `cancel` as well as `close`
    // because `cancel` is synchronous — React commits before the browser's own close paints.
    const handleDialogClose = (event: React.SyntheticEvent<HTMLDialogElement>) => {
        // A close request while a prompt is up inside the viewer declines the prompt and stays
        // fullscreen (Escape with focus in the ordinal reaches only this dialog's cancel).
        const state = useStore.getState()
        if (event.type === 'cancel' && isTrayOpenIn(state, event.currentTarget)) {
            event.preventDefault()
            state.actions.trayActions.closeTray()
            return
        }
        setIsFullscreen(false)
    }

    const handleViewerClick = (event: React.MouseEvent) => {
        event.stopPropagation()
        // Only the viewer's own safe-area margin is clickable here; the iframe covers the rest
        // and keeps its clicks. Tapping that margin exits, like the old overlay backdrop.
        if (isFullscreen && event.target === event.currentTarget) setIsFullscreen(false)
    }

    const playControl = (
        <div className={styles.previewOverlayButtonContainer}>
            <MediaPlayButton
                onClick={handleRequestLive}
                onMouseDown={event => event.stopPropagation()}
                onPointerDown={event => event.stopPropagation()}
                onTouchStart={handleRequestLive}
                aria-label="Load live preview"
                title="Load live preview"
            />
        </div>
    )
    const playHint = <span className={styles.placeholderHint}>Load live preview</span>

    return (
        <div
            ref={wrapperRef}
            className={cn(styles.ordinalHtmlWrapper, className)}
            data-no-modal-interaction
            data-post-embed-navigation-exempt="true"
        >
            {shouldShowPreview && (
                <div
                    className={styles.previewImageContainer}
                    data-no-modal-interaction
                    onClick={handleRequestLive}
                >
                    <img
                        src={resolvedPreviewUrl as string}
                        alt={title}
                        className={styles.previewImage}
                        loading="lazy"
                        decoding="async"
                        onError={event => {
                            try {
                                event.stopPropagation()
                            } catch {}
                            if (previewErrorHandledRef.current.has(src)) return
                            previewErrorHandledRef.current.add(src)
                            // Drop the broken preview. Passive feed cards keep their reserved frame
                            // blank; interactive surfaces can reveal live HTML.
                            setResolvedPreviewUrl(null)
                            setPreviewLoadFailed(true)
                            onPreviewPending?.()
                            // A stopped card stays stopped: only play brings the ordinal back.
                            if (!stoppedByUser && (forceLive || userActivatedRef.current)) {
                                setShowLiveEmbed(true)
                            } else {
                                setShowLiveEmbed(false)
                            }
                        }}
                    />
                    {playControl}
                </div>
            )}

            {shouldShowPlaceholder && (
                // Same matte as the screenshot state: a static card holding the play control.
                <div
                    className={cn(styles.previewImageContainer, fromLinkPreview && styles.inThread)}
                    data-no-modal-interaction
                    data-testid={
                        stoppedByUser
                            ? 'html-ordinal-stopped-preview'
                            : 'html-ordinal-placeholder-preview'
                    }
                    onClick={handleRequestLive}
                >
                    {playControl}
                    {playHint}
                </div>
            )}

            {shouldShowLiveSurface && (
                // A <dialog> so fullscreen can promote this very element into the top layer; while
                // closed it lays out as the plain viewer block (see the stylesheet).
                <dialog
                    ref={setViewerDialogRef}
                    tabIndex={-1}
                    className={cn(styles.viewerContainer, fromLinkPreview && styles.inThread)}
                    // Closed, it is a card, not a dialog: keep assistive tech from announcing one
                    // per post. Open, the native role returns.
                    role={isFullscreen ? undefined : 'group'}
                    aria-label={isFullscreen ? 'Fullscreen preview' : title}
                    data-no-modal-interaction
                    data-ordinal-viewer
                    data-testid="html-ordinal-live-preview"
                    style={{ pointerEvents: 'auto' }}
                    onClick={handleViewerClick}
                    onCancel={handleDialogClose}
                    onClose={handleDialogClose}
                    onMouseDown={event => event.stopPropagation()}
                    onPointerDown={event => event.stopPropagation()}
                    onTouchStart={event => event.stopPropagation()}
                >
                    <HTMLOrdinalRender
                        src={liveEmbedSrc}
                        className={styles.liveEmbed}
                        allowInteraction={!touchSettling}
                        title={title}
                        htmlAlign={htmlAlign}
                        walletBridge={walletBridge}
                    />
                    {isFullscreen && (
                        <div className={styles.fullscreenControls}>
                            <button
                                type="button"
                                className={styles.fullscreenControlButton}
                                onClick={handleStopLive}
                                aria-label="Stop preview"
                                title="Stop preview"
                            >
                                <Square size={18} strokeWidth={2.2} fill="currentColor" />
                            </button>
                            <button
                                type="button"
                                className={styles.fullscreenControlButton}
                                onClick={handleExitFullscreen}
                                aria-label="Exit fullscreen"
                                title="Exit fullscreen"
                            >
                                <X size={20} strokeWidth={2.2} />
                            </button>
                        </div>
                    )}
                </dialog>
            )}

            {showLiveEmbed && (
                <>
                    {!isFullscreen && (
                        <div className={styles.previewControls}>
                            <button
                                type="button"
                                className={styles.previewControlButton}
                                onClick={handleStopLive}
                                aria-label="Stop preview"
                                title="Stop preview"
                            >
                                <Square size={16} strokeWidth={2.2} fill="currentColor" />
                            </button>
                            {supportsTopLayerDialog && (
                                <button
                                    ref={fullscreenButtonRef}
                                    type="button"
                                    className={styles.previewControlButton}
                                    onClick={handleEnterFullscreen}
                                    aria-label="Fullscreen"
                                    title="Fullscreen"
                                >
                                    <Maximize2 size={18} strokeWidth={2.2} />
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default React.memo(HTMLOrdinalLoader)
